<template>
  <div class="aspect-video" :class="{ [dimensions.classes]: size }">
    <RevButtonBase
      v-if="showPlaceholder"
      class="relative h-full w-full"
      data-test="youtube-facade"
      @click="onPlaceholderClick"
    >
      <ImageRenderer
        v-if="image"
        :alt="title"
        :height="dimensions.height"
        :src="image.src"
        :src-mobile="image.srcMobile"
        :width="dimensions.width"
      />

      <ImageRenderer
        v-else
        :alt="title"
        :height="dimensions.height"
        :src="`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`"
        :src-mobile="`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`"
        :width="dimensions.width"
      />

      <RevIllustration
        alt=""
        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        :height="48"
        src="/img/cms/YoutubePlay.svg"
        :width="68"
      />
    </RevButtonBase>

    <iframe
      v-else
      allow="autoplay; encrypted-media"
      allowfullscreen
      class="aspect-video w-full"
      data-test="youtube-iframe"
      frameborder="0"
      loading="lazy"
      :src="`https://www.youtube.com/embed/${youtubeId}?autoplay=1`"
      :title
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { Image } from '@backmarket/http-api/src/api-specs-content/models/image'
import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import type { YoutubeVideo } from '@backmarket/http-api/src/api-specs-content/models/video'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'

import ImageRenderer from '../ImageRenderer/ImageRenderer.vue'

type YoutubeEmbeddedProps = {
  youtubeId: string
  title?: string
  size?: YoutubeVideo['size']
  image?: Image
  tracking?: Tracking
}

const props = withDefaults(defineProps<YoutubeEmbeddedProps>(), {
  title: '',
  placeholderAttrs: undefined,
  size: undefined,
  image: undefined,
  tracking: undefined,
})

const { trackClick } = useTracking()

const showPlaceholder = ref<boolean>(true)

const dimensions = computed(() => {
  if (props.size === 'medium') {
    return {
      height: 630,
      width: 1120,
      classes: tw`w-[1120px]`,
    }
  }

  return {
    height: 415,
    width: 736,
    classes: tw`w-[736px]`,
  }
})

function onPlaceholderClick() {
  showPlaceholder.value = false

  trackClick(
    removeEmptyValuesInObject({
      ...props.tracking,
      blockTitle: props.title,
    }),
  )
}
</script>
